import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";
import {
  BooklistBooksSection,
  BooklistFiltersPanel,
  BooklistHeader,
  BooklistInstructions,
  BooklistResultHeader,
  CurriculumSection,
  getCurrentSchoolYear,
} from "#/components/booklist";
import type {
  BooklistBook,
  BooklistFilterOptions,
  BooklistFilters,
} from "#/components/booklist/types";
import { useBooklist } from "#/queries/booklist";
import { type Product, useProducts } from "#/queries/products";
import { useCart, useSetFamilyBook } from "#/store/cart";

// /booklist is reached by direct URL only (no inbound nav link passes params),
// so it owns a minimal search schema — grade/curriculum/subject — fully
// decoupled from the shop's shopSearchSchema. Selection persists in the URL;
// the tier-grouped results render after Submit.
const booklistSearchSchema = z.object({
  grade: z.string().min(1).optional(),
  curriculum: z.string().min(1).optional(),
  subject: z.string().min(1).optional(),
});
type BooklistSearch = z.infer<typeof booklistSearchSchema>;

export const Route = createFileRoute("/booklist")({
  validateSearch: booklistSearchSchema,
  head: () => ({ meta: [{ title: "Dynamic Booklist" }] }),
  component: BooklistPage,
});

const EMPTY_FILTERS: BooklistFilters = {
  gradeLevel: "",
  curriculumAlignment: "",
  subject: "",
};

function toBook(p: Product): BooklistBook {
  return {
    id: p.id,
    productId: p.id,
    title: p.title,
    priceAmount: p.metadata.priceAmount,
    currency: p.metadata.currency,
    imageUrl: p.thumbnail ?? (p.images ?? [])[0]?.url ?? null,
    variantId: (p.variants ?? [])[0]?.id ?? null,
    variant: (p.variants ?? [])[0] ?? null,
    slug: p.metadata.slug,
  };
}

function BooklistPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const [filters, setFilters] = useState<BooklistFilters>(() => ({
    gradeLevel: search.grade ?? "",
    curriculumAlignment: search.curriculum ?? "",
    subject: search.subject ?? "",
  }));
  // A complete triple present in the URL on load means we can show results
  // immediately (e.g. a shared/bookmarked booklist).
  const [submitted, setSubmitted] = useState(
    () => !!(search.grade && search.curriculum && search.subject),
  );

  const allSelected =
    !!filters.gradeLevel && !!filters.curriculumAlignment && !!filters.subject;

  const { data: cube } = useBooklist({
    grade: filters.gradeLevel || undefined,
    curriculum: filters.curriculumAlignment || undefined,
    subject: filters.subject || undefined,
  });

  const filterList: BooklistFilterOptions | null = cube
    ? {
        gradeLevels: cube.options.grades,
        curriculumAlignments: cube.options.curricula,
        subjects: cube.options.subjects,
      }
    : null;

  const sections = submitted && allSelected ? cube?.sections ?? null : null;
  const allIds = sections
    ? [
        ...new Set([
          ...sections.recommended,
          ...sections.alternative,
          ...sections.supplemental,
          ...sections.family,
        ]),
      ]
    : [];

  const { data: hydrated } = useProducts(
    { ids: allIds, limit: allIds.length || 1 },
    { enabled: allIds.length > 0, keepPreviousData: true },
  );
  const byId = new Map((hydrated?.items ?? []).map((p) => [p.id, p]));
  const booksFor = (ids: string[]): BooklistBook[] =>
    ids
      .map((id) => byId.get(id))
      .filter((p): p is Product => p != null)
      .map(toBook);

  const recommendedList = booksFor(sections?.recommended ?? []);
  const alternativeList = booksFor(sections?.alternative ?? []);
  const supplementalList = booksFor(sections?.supplemental ?? []);
  const familyBooksList = booksFor(sections?.family ?? []);

  const { data: cart } = useCart();
  const setFamilyBook = useSetFamilyBook();
  const familyVariantIds = new Set(
    familyBooksList
      .map((b) => b.variantId)
      .filter((v): v is string => v != null),
  );
  const selectedFamilyVariantId =
    cart?.items?.find(
      (l) => l.variant_id != null && familyVariantIds.has(l.variant_id),
    )?.variant_id ?? null;

  const writeSearch = (next: BooklistSearch) => {
    navigate({ search: next, replace: true, resetScroll: false });
  };

  const handleFilterChange = (patch: Partial<BooklistFilters>) => {
    let updated = { ...filters, ...patch };
    if (
      patch.gradeLevel !== undefined &&
      patch.gradeLevel !== filters.gradeLevel
    ) {
      updated = { ...updated, curriculumAlignment: "", subject: "" };
    } else if (
      patch.curriculumAlignment !== undefined &&
      patch.curriculumAlignment !== filters.curriculumAlignment
    ) {
      updated = { ...updated, subject: "" };
    }
    setFilters(updated);
    setSubmitted(false);
    writeSearch({
      grade: updated.gradeLevel || undefined,
      curriculum: updated.curriculumAlignment || undefined,
      subject: updated.subject || undefined,
    });
  };

  const clearAllFilters = () => {
    setFilters(EMPTY_FILTERS);
    setSubmitted(false);
    writeSearch({});
  };

  const activeFilterCount =
    (filters.gradeLevel ? 1 : 0) +
    (filters.curriculumAlignment ? 1 : 0) +
    (filters.subject ? 1 : 0);

  const subjectLabel =
    filterList?.subjects.find((s) => s.value === filters.subject)?.label ??
    filters.subject;

  if (!filterList) return null;

  return (
    <main className="space-y-6 md:space-y-10">
      <BooklistHeader />

      <BooklistInstructions />

      <BooklistFiltersPanel
        filters={filters}
        options={filterList}
        onFilterChange={handleFilterChange}
        onSubmit={() => setSubmitted(true)}
        onClearAll={clearAllFilters}
        activeFilterCount={activeFilterCount}
      />

      {submitted && (
        <BooklistResultHeader
          gradeLabel={filters.gradeLevel}
          subjectLabel={subjectLabel}
          schoolYear={getCurrentSchoolYear()}
        />
      )}

      <div className="space-y-0">
        <CurriculumSection
          label="Recommended"
          books={recommendedList}
          filtersSubmitted={submitted}
          showDivider
        />
        <CurriculumSection
          label="Alternative"
          books={alternativeList}
          filtersSubmitted={submitted}
        />
      </div>

      <div className="space-y-0 pb-8 md:pb-24">
        <BooklistBooksSection
          title="Supplemental"
          subtitle="Please select title/s from the Supplemental category to further enhance the learning & reading experience."
          books={supplementalList}
        />
        <BooklistBooksSection
          title="Family Books"
          subtitle="Kindly select only one family book."
          books={familyBooksList}
          selectable
          selectedVariantId={selectedFamilyVariantId}
          onSelect={(v) => setFamilyBook(v, familyVariantIds)}
        />
      </div>
    </main>
  );
}
